import React, {useState, useEffect} from 'react';
import {Popconfirm} from 'antd/lib';
import {Button, Tooltip} from 'antd';
import {ReactComponent as Close} from '../assets/icons/Close.svg';
import {ReactComponent as Undo} from '../assets/icons/Undo.svg';
import LoaderButton from './LoaderButton';
import {ReactComponent as Reset} from '../assets/icons/Reset.svg';
import {ReactComponent as Redo} from '../assets/icons/Redo.svg';
import {ReactComponent as Check} from '../assets/icons/Check.svg';
import {ReactComponent as Print} from '../assets/icons/Print.svg';
import {ReactComponent as Edit} from '../assets/icons/Edit.svg';
import {Prompt, useHistory} from 'react-router-dom';
import EmailButton from './EmailButton';
import DownloadButton from './DownloadButton';
import {noop} from 'lodash';

function SaveMenu({
                      editMode,
                      setEditMode,
                      modelInstance,
                      initialInstanceRef,
                      controls,
                      save,
                      updated,
                      id,
                      disableEditForRow,
                      setPrint,
                      showPrint,
                      closeView,
                      showSend,
                      showMessageFieldInEmail = true,
                      mailData,
                      modelService,
                      setIsCancelling = noop,
                      isCancelling,
                      listUrl,
                      validFn,
                      files,
                      onDownloadClicked,
                      isCharter = false,
                      createPrintPDF = null,
                      children,
                      editControls,
                      viewControls,
                      isSaving = false,
                      forceListNavigation = false
                  }) {

    const [confirmedExit, setConfirmedExit] = useState(false);
    const history = useHistory();
    
    // Define confirmation message as a constant for reuse
    const unsavedChangesMessage = 'You have unsaved changes. Are you sure you want to leave?';

    // Add keyboard shortcut listener for 'e' to toggle edit mode
    useEffect(() => {
        const handleKeyPress = (event) => {
            // Only trigger if not in edit mode and key pressed is 'e'
            if (!editMode && event.key === 'e' && !event.ctrlKey && !event.altKey && !event.metaKey) {
                // Only trigger if no input/textarea is focused
                const activeElement = document.activeElement;
                const isInputFocused = activeElement.tagName === 'INPUT' || 
                                       activeElement.tagName === 'TEXTAREA' || 
                                       activeElement.isContentEditable;
                
                if (!isInputFocused) {
                    initialInstanceRef.current = modelInstance.clone();
                    controls.reset(initialInstanceRef.current);
                    setEditMode(true);
                    event.preventDefault();
                }
            }
            
            // Add shortcut for saving with Ctrl+S or Command+S
            if (editMode && event.key === 's' && (event.ctrlKey || event.metaKey) && (!validFn || validFn())) {
                event.preventDefault();
                save(modelInstance, true);
            }
            
            // Add shortcut for closing with Escape key
            if (editMode && (event.key === 'Escape' || event.key === 'Esc')) {
                event.preventDefault();
                
                const handleCloseAction = () => {
                    setEditMode(false);
                    setConfirmedExit(true);
                    
                    if (id === '_new_' || forceListNavigation) {
                        // Hack to fix double confirmation
                        setTimeout(() => history.push(listUrl), 1);
                    } else {
                        setTimeout(() => controls.go(-controls.backLength), 1);
                    }
                };
                
                const handleCloseWithoutChanges = () => {
                    setEditMode(false);
                    if (id === '_new_' || forceListNavigation) {
                        history.push(listUrl);
                    } else {
                        controls.go(-controls.backLength);
                    }
                };
                
                // Only show confirmation if there are unsaved changes
                if (controls.backLength > 0 || updated) {
                    // Show confirmation dialog programmatically
                    if (window.confirm(unsavedChangesMessage)) {
                        handleCloseAction();
                    }
                } else {
                    handleCloseWithoutChanges();
                }
            }
        };

        // Add the event listener
        document.addEventListener('keydown', handleKeyPress);
        
        // Clean up the event listener
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [editMode, modelInstance, initialInstanceRef, controls, setEditMode, save, validFn, updated, id, listUrl, history]);

    const handlePrint = async () => {
        if (createPrintPDF && typeof createPrintPDF === 'function') {
            try {
                const pdfFile = await createPrintPDF();
                const pdfUrl = URL.createObjectURL(pdfFile);
                const printWindow = window.open(pdfUrl, '_blank');
                if(printWindow){
                    printWindow.onload = function() {
                        printWindow.print();
                        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
                    };
                }
            } catch (error) {
                console.error('Error printing PDF:', error);
                window.print();
            }
        } else {
            window.print();
        }
    }

    const promptExit = () => updated && !confirmedExit;
    return (<>{

        editMode ?
            <div className="d-flex align-items-center justify-content-between page-main-controls top-float">
                {/*<div className="d-flex align-items-center justify-content-between page-main-controls mb-0">*/}

                <Prompt
                    when={updated && !confirmedExit}
                    message={unsavedChangesMessage}
                />

                <div className="page-main-controls">
                    <div className="d-flex page-sec-controls">
                        {children}
                        <Button type="primary" className="icon-button btn-filled" icon={<Undo/>}
                                disabled={!controls.canUndo || (disableEditForRow && disableEditForRow())}
                                onClick={() => {
                                    controls.undo();
                                }}
                        >Undo {isCharter ? '(Beta)' : null} ({controls.backLength})</Button>
                        <Popconfirm
                            title="Reset all changes"
                            description="Reset all changes since loading?"
                            onConfirm={() => {
                                setIsCancelling(true);
                                if (id === '_new_') {
                                    modelService.delete(id, true).then(() => {
                                        console.log('Deleted new sb.');
                                    }).catch(e => {
                                        console.log(e);
                                    }).finally(() => {
                                        setIsCancelling(false);
                                        setEditMode(false);
                                        history.push(listUrl);
                                    });
                                } else if (initialInstanceRef.current) {
                                    save(initialInstanceRef.current).then(() => {
                                        console.log('Saved original sb.');
                                        controls.reset(initialInstanceRef.current);
                                    }).catch(e => {
                                        console.log(e);
                                    }).finally(() => {
                                        setIsCancelling(false);
                                        setEditMode(false);
                                    });
                                } else {
                                    setIsCancelling(false);
                                    setEditMode(false);
                                }
                            }}
                            okText="Yes"
                            cancelText="No">
                            {!isCharter &&
                                <LoaderButton
                                    isLoading={isCancelling}
                                    type="primary"
                                    icon={<Reset/>}
                                    disabled={controls.backLength === 0}
                                    className="icon-button btn-filled btn-error"
                                >Reset</LoaderButton>
                            }
                        </Popconfirm>
                        <Button type="primary" className="icon-button btn-filled" icon={<Redo/>}
                                disabled={!controls.canRedo || (disableEditForRow && disableEditForRow())}
                                onClick={() => {
                                    controls.redo();
                                }}
                        >Redo {isCharter ? '(Beta)' : null} ({controls.forwardLength})</Button>
                        {editControls}
                        {controls.backLength > 0 || updated ?
                            <Popconfirm title={unsavedChangesMessage}
                                        onConfirm={e => {
                                            setEditMode(false);
                                            setConfirmedExit(true);
                                            if (id === '_new_') {
                                                // Hack to fix double confirmation
                                                setTimeout(() => history.push(listUrl), 1);
                                            } else {
                                                setTimeout(() => controls.go(-controls.backLength), 1);
                                            }
                                        }}>
                                <Button type="primary" className="icon-button btn-filled btn-light icon-10"
                                        icon={<Close/>}
                                > Close</Button></Popconfirm> :
                            <Button type="primary" className="icon-button btn-filled btn-light icon-10"
                                    icon={<Close/>}
                                    onClick={() => {
                                        setEditMode(false);
                                        if (id === '_new_') {
                                            history.push(listUrl);
                                        } else {
                                            controls.go(-controls.backLength);
                                        }
                                    }}
                            > Close</Button>
                        }
                        <Tooltip
                            title={validFn && !validFn() ? 'Please fill all required fields before continuing.' : ''}>
                            <LoaderButton type="primary" className="icon-button btn-filled btn-success"
                                    icon={<Check/>}
                                    disabled={validFn && !validFn()}
                                    onClick={() => {
                                        save(modelInstance, true);
                                    }}
                                    isLoading={isSaving}
                            >Save</LoaderButton></Tooltip>
                    </div>
                </div>
            </div>
            :
            <div className="d-flex align-items-center justify-content-between page-main-controls top-float">
                <div className="d-flex page-sec-controls">
                    {viewControls}
                    {setPrint ?
                        <Button type="primary" className="icon-button btn-filled" icon={<Print/>}
                                onClick={async event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setPrint(true);
                                }}>Print</Button> :
                        <></>}
                    {showPrint ?
                        <Button type="primary" className="icon-button btn-filled" icon={<Print/>} onClick={handlePrint}>Print</Button> :
                        <></>}
                    {showSend ? <EmailButton mail={mailData} showMessage={showMessageFieldInEmail}/> : <></>}
                    {files?.length || onDownloadClicked ?
                        <DownloadButton files={files} onDownloadClicked={onDownloadClicked}/> : <></>}
                    {closeView ?
                        <Button type="primary" className="icon-button btn-filled btn-light icon-10"
                                icon={<Close/>}
                                onClick={() => {
                                    history.push(listUrl);
                                }}
                        >Close</Button>
                        : <></>}
                    <Button type="primary" className="icon-button btn-filled" onClick={e => {
                        initialInstanceRef.current = modelInstance.clone();
                        controls.reset(initialInstanceRef.current);
                        setEditMode(true);
                    }}
                            icon={<Edit/>}>Edit</Button>
                </div>
            </div>
    }</>);
}

export default SaveMenu;
